import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, CreditCard, Key, Bell, Shield, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Chrome, Github, Facebook, Twitter } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import BillingTab from '../components/settings/BillingTab';
import ApiKeysTab from '../components/settings/ApiKeysTab';
import SecurityTab from '../components/settings/SecurityTab';
import DomainTab from '../components/settings/DomainTab';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [buyingCredits, setBuyingCredits] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paypalConfigured, setPaypalConfigured] = useState(false);
  const [customCredits, setCustomCredits] = useState('');
  const [customBuying, setCustomBuying] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: user?.name || '', email: user?.email || '' });
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [notifications, setNotifications] = useState({ email: true, push: false, updates: true });
  const [notifLoaded, setNotifLoaded] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);
  const [apiKeys, setApiKeys] = useState({ openai: '', anthropic: '', xai: '' });
  const [apiKeyStatus, setApiKeyStatus] = useState({ has_openai: false, has_anthropic: false, has_xai: false });
  const [savingKey, setSavingKey] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState({ google: false, github: false, facebook: false, twitter: false });
  const [customDomain, setCustomDomain] = useState('');
  const [domainSaved, setDomainSaved] = useState(false);
  const [savingDomain, setSavingDomain] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookType, setWebhookType] = useState('generic');
  const [webhookSaved, setWebhookSaved] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);

  const displayUser = user || { name: 'User', email: '', plan: 'Free', credits: 0 };
  const maxCredits = displayUser.plan === 'Pro' ? 500 : displayUser.plan === 'Enterprise' ? 9999 : 50;
  const creditPercent = Math.min(100, ((displayUser.credits ?? 0) / maxCredits) * 100);
  const inputClass = "bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-blue-500/50";

  const fetchSettings = useCallback(async () => {
    try {
      const [notifRes, keysRes, accountsRes, domainRes, paypalRes, webhookRes] = await Promise.all([
        api.get('/user/notifications'),
        api.get('/user/api-keys'),
        api.get('/user/connected-accounts'),
        api.get('/user/domain'),
        api.get('/paypal/status').catch(() => ({ data: { configured: false } })),
        api.get('/user/webhooks').catch(() => ({ data: { url: '', type: 'generic' } }))
      ]);
      setNotifications(notifRes.data);
      setNotifLoaded(true);
      setApiKeyStatus({ has_openai: keysRes.data.has_openai, has_anthropic: keysRes.data.has_anthropic, has_xai: keysRes.data.has_xai });
      setConnectedAccounts(accountsRes.data);
      if (domainRes.data.domain) { setCustomDomain(domainRes.data.domain); setDomainSaved(true); }
      setPaypalConfigured(paypalRes.data.configured);
      if (webhookRes.data.url) { setWebhookUrl(webhookRes.data.url); setWebhookType(webhookRes.data.type || 'generic'); setWebhookSaved(true); }
    } catch (err) { console.error('Failed to load settings:', err); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/auth/profile', { name: profileForm.name });
      await refreshUser();
      toast({ title: 'Profile Updated', description: 'Your profile has been saved.' });
    } catch { toast({ title: 'Error', description: 'Failed to save profile.', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPass !== passwordForm.confirm) { toast({ title: 'Error', description: 'Passwords do not match.', variant: 'destructive' }); return; }
    if (passwordForm.newPass.length < 6) { toast({ title: 'Error', description: 'Password must be at least 6 characters.', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await api.put('/auth/password', { current_password: passwordForm.current, new_password: passwordForm.newPass });
      toast({ title: 'Password Updated' });
      setPasswordForm({ current: '', newPass: '', confirm: '' });
    } catch (err) { toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to update password.', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const handleSaveNotifications = async (newNotifs) => {
    setNotifications(newNotifs);
    setSavingNotif(true);
    try { await api.put('/user/notifications', newNotifs); toast({ title: 'Preferences Saved' }); }
    catch { toast({ title: 'Error', description: 'Failed to save preferences.', variant: 'destructive' }); }
    finally { setSavingNotif(false); }
  };

  const handleSaveApiKey = async (provider) => {
    if (!apiKeys[provider].trim()) { toast({ title: 'Error', description: 'Please enter a valid key.', variant: 'destructive' }); return; }
    setSavingKey(provider);
    try {
      await api.put('/user/api-keys', { provider, key: apiKeys[provider] });
      setApiKeyStatus(prev => ({ ...prev, [`has_${provider}`]: true }));
      setApiKeys(prev => ({ ...prev, [provider]: '' }));
      toast({ title: 'API Key Saved', description: `Your ${provider} key has been saved securely.` });
    } catch { toast({ title: 'Error', description: 'Failed to save API key.', variant: 'destructive' }); }
    finally { setSavingKey(null); }
  };

  const handleRemoveApiKey = async (provider) => {
    setSavingKey(provider);
    try {
      await api.put('/user/api-keys', { provider, key: '' });
      setApiKeyStatus(prev => ({ ...prev, [`has_${provider}`]: false }));
      toast({ title: 'Key Removed', description: `Your ${provider} key has been removed.` });
    } catch { toast({ title: 'Error', description: 'Failed to remove key.', variant: 'destructive' }); }
    finally { setSavingKey(null); }
  };

  const handleBuyCredits = async (packageId) => {
    setBuyingCredits(packageId);
    try {
      if (paymentMethod === 'paypal' && paypalConfigured) {
        const response = await api.post('/paypal/create-order', { package_id: packageId, base_url: window.location.origin });
        if (response.data.approval_url) {
          sessionStorage.setItem('paypal_package_id', packageId);
          sessionStorage.setItem('paypal_payment_id', response.data.payment_id);
          window.location.href = response.data.approval_url;
        }
      } else {
        const response = await api.post('/checkout/credits', { package_id: packageId });
        if (response.data.url) window.location.href = response.data.url;
      }
    } catch (err) { toast({ title: 'Payment Error', description: err.response?.data?.detail || 'Payment failed.', variant: 'destructive' }); }
    finally { setBuyingCredits(null); }
  };

  const handleCustomCredits = async () => {
    const amount = parseInt(customCredits);
    if (!amount || amount < 10) { toast({ title: 'Error', description: 'Minimum 10 credits', variant: 'destructive' }); return; }
    setCustomBuying(true);
    try {
      if (paymentMethod === 'paypal' && paypalConfigured) {
        const response = await api.post('/paypal/create-order', { package_id: 'custom', custom_credits: amount, base_url: window.location.origin });
        if (response.data.approval_url) {
          sessionStorage.setItem('paypal_package_id', 'custom');
          sessionStorage.setItem('paypal_payment_id', response.data.payment_id);
          window.location.href = response.data.approval_url;
        }
      } else {
        const response = await api.post('/checkout/credits', { package_id: 'custom', custom_credits: amount });
        if (response.data.url) window.location.href = response.data.url;
      }
    } catch (err) { toast({ title: 'Payment Error', description: err.response?.data?.detail || 'Failed.', variant: 'destructive' }); }
    finally { setCustomBuying(false); }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText('stk_live_supertool_universal_key');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: 'Copied' });
  };

  const handleSaveWebhook = async () => {
    setSavingWebhook(true);
    try {
      await api.put('/user/webhooks', { url: webhookUrl.trim(), type: webhookType });
      setWebhookSaved(true);
      toast({ title: 'Webhook Saved', description: `${webhookType.charAt(0).toUpperCase() + webhookType.slice(1)} webhook configured` });
    } catch { toast({ title: 'Error', description: 'Failed to save webhook', variant: 'destructive' }); }
    finally { setSavingWebhook(false); }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    try {
      const res = await api.post('/notifications/test-webhook');
      if (res.data.success) toast({ title: 'Test Sent', description: 'Check your webhook destination for the test message' });
      else toast({ title: 'Test Failed', description: res.data.message, variant: 'destructive' });
    } catch (err) { toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to test webhook', variant: 'destructive' }); }
    finally { setTestingWebhook(false); }
  };

  const handleConnectAccount = async (provider) => {
    const redirectBase = window.location.origin;
    if (provider === 'google') {
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=705819118898-i3c8gftmfrt4t556uvc6o2trd7sc0pn0.apps.googleusercontent.com&redirect_uri=${encodeURIComponent(redirectBase + '/auth/callback')}&response_type=code&scope=${encodeURIComponent('email profile')}&access_type=offline&prompt=consent`;
    } else if (provider === 'github') {
      window.location.href = `https://github.com/login/oauth/authorize?client_id=Ov23li4YTa7pcMFK3IoB&redirect_uri=${encodeURIComponent(redirectBase + '/auth/callback/github')}&scope=${encodeURIComponent('user:email read:user')}`;
    } else if (provider === 'facebook') {
      window.location.href = `https://www.facebook.com/v19.0/dialog/oauth?client_id=1241788857929785&redirect_uri=${encodeURIComponent(redirectBase + '/auth/callback/facebook')}&scope=${encodeURIComponent('email,public_profile')}&response_type=code`;
    } else if (provider === 'twitter') {
      const clientId = 'a3ZuMF9vYTBTdVRBU1V4d1BHQW46MTpjaQ';
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      const codeVerifier = btoa(String.fromCharCode(...array)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      const encoder = new TextEncoder();
      const data = encoder.encode(codeVerifier);
      const digest = await crypto.subtle.digest('SHA-256', data);
      const codeChallenge = btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      sessionStorage.setItem('twitter_code_verifier', codeVerifier);
      const state = btoa(Math.random().toString(36));
      sessionStorage.setItem('twitter_oauth_state', state);
      window.location.href = `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectBase + '/auth/callback/twitter')}&scope=${encodeURIComponent('tweet.read users.read offline.access')}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
    }
  };

  const handleSaveDomain = async () => {
    if (!customDomain.trim()) { toast({ title: 'Error', description: 'Please enter a domain.', variant: 'destructive' }); return; }
    setSavingDomain(true);
    try {
      await api.put('/user/domain', { domain: customDomain.trim() });
      setDomainSaved(true);
      toast({ title: 'Domain Saved', description: 'Your custom domain has been saved.' });
    } catch { toast({ title: 'Error', description: 'Failed to save domain.', variant: 'destructive' }); }
    finally { setSavingDomain(false); }
  };

  const creditPackages = [
    { id: 'credits_275', credits: 275, price: '$55', per: '$0.20/credit' },
    { id: 'credits_550', credits: 550, price: '$110', per: '$0.20/credit' },
    { id: 'credits_1150', credits: 1150, price: '$230', per: '$0.20/credit' },
    { id: 'credits_3000', credits: 3000, price: '$600', per: '$0.20/credit' }
  ];

  const socialProviders = [
    { key: 'google', name: 'Google', icon: Chrome, color: 'text-blue-400' },
    { key: 'github', name: 'GitHub', icon: Github, color: 'text-foreground/80' },
    { key: 'twitter', name: 'Twitter/X', icon: Twitter, color: 'text-sky-400' },
    { key: 'facebook', name: 'Facebook', icon: Facebook, color: 'text-blue-500' }
  ];

  const apiProviders = [
    { key: 'openai', name: 'OpenAI', placeholder: 'sk-...', desc: 'Your own GPT API key' },
    { key: 'anthropic', name: 'Anthropic', placeholder: 'sk-ant-...', desc: 'Your own Claude API key' },
    { key: 'xai', name: 'xAI', placeholder: 'xai-...', desc: 'Your own Grok API key' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-2 text-foreground/80 hover:text-foreground hover:bg-accent" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account and preferences</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-muted border border-border">
            <TabsTrigger value="profile" className="gap-2 text-muted-foreground data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-profile"><User className="w-4 h-4" />Profile</TabsTrigger>
            <TabsTrigger value="billing" className="gap-2 text-muted-foreground data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-billing"><CreditCard className="w-4 h-4" />Billing</TabsTrigger>
            <TabsTrigger value="api" className="gap-2 text-muted-foreground data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-api"><Key className="w-4 h-4" />API Keys</TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2 text-muted-foreground data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-notifications"><Bell className="w-4 h-4" />Notifications</TabsTrigger>
            <TabsTrigger value="security" className="gap-2 text-muted-foreground data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-security"><Shield className="w-4 h-4" />Security</TabsTrigger>
            <TabsTrigger value="domain" className="gap-2 text-muted-foreground data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-domain"><Globe className="w-4 h-4" />Domain</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground">Personal Information</CardTitle>
                <CardDescription className="text-muted-foreground">Update your personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="settings-name" className="text-foreground/80">Full Name</Label>
                    <Input id="settings-name" data-testid="profile-name-input" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} className={`mt-1 ${inputClass}`} />
                  </div>
                  <div>
                    <Label htmlFor="settings-email" className="text-foreground/80">Email</Label>
                    <Input id="settings-email" data-testid="profile-email-input" type="email" value={profileForm.email} disabled className={`mt-1 ${inputClass} opacity-60 cursor-not-allowed`} />
                    <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                  </div>
                </div>
                <Button data-testid="save-profile-btn" onClick={handleSaveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-500">{saving ? 'Saving...' : 'Save Changes'}</Button>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardHeader><CardTitle className="text-foreground">Account Overview</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-muted rounded-xl border border-border text-center">
                    <div className="text-3xl font-bold text-foreground" data-testid="account-credits">{displayUser.credits ?? 0}</div>
                    <div className="text-sm text-muted-foreground mt-1">AI Credits</div>
                  </div>
                  <div className="p-4 bg-muted rounded-xl border border-border text-center">
                    <div className="text-3xl font-bold text-blue-400" data-testid="account-plan">{displayUser.plan}</div>
                    <div className="text-sm text-muted-foreground mt-1">Current Plan</div>
                  </div>
                  <div className="p-4 bg-muted rounded-xl border border-border text-center">
                    <div className="text-3xl font-bold text-emerald-400" data-testid="account-projects">{displayUser.projectsCount || 0}</div>
                    <div className="text-sm text-muted-foreground mt-1">Projects</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <BillingTab
              displayUser={displayUser} maxCredits={maxCredits} creditPercent={creditPercent}
              paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
              paypalConfigured={paypalConfigured} creditPackages={creditPackages}
              buyingCredits={buyingCredits} handleBuyCredits={handleBuyCredits}
              customCredits={customCredits} setCustomCredits={setCustomCredits}
              customBuying={customBuying} handleCustomCredits={handleCustomCredits} navigate={navigate}
            />
          </TabsContent>

          {/* API Keys Tab */}
          <TabsContent value="api">
            <ApiKeysTab
              showKey={showKey} setShowKey={setShowKey} copied={copied} handleCopyKey={handleCopyKey}
              apiProviders={apiProviders} apiKeyStatus={apiKeyStatus} apiKeys={apiKeys} setApiKeys={setApiKeys}
              savingKey={savingKey} handleSaveApiKey={handleSaveApiKey} handleRemoveApiKey={handleRemoveApiKey}
            />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground">Notification Preferences</CardTitle>
                <CardDescription className="text-muted-foreground">Choose how you want to be notified. Changes are saved automatically.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {[
                  { key: 'email', title: 'Email Notifications', desc: 'Deploy status, credit alerts, and receipts' },
                  { key: 'push', title: 'Push Notifications', desc: 'Real-time alerts on your device' },
                  { key: 'updates', title: 'Product Updates', desc: 'New features, models, and improvements' }
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between p-4 bg-muted rounded-lg border border-border" data-testid={`notification-${item.key}`}>
                    <div>
                      <h4 className="font-medium text-foreground">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                    <Switch checked={notifications[item.key]} onCheckedChange={(checked) => handleSaveNotifications({ ...notifications, [item.key]: checked })} disabled={savingNotif || !notifLoaded} data-testid={`switch-${item.key}`} />
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-foreground">Webhook Notifications</CardTitle>
                <CardDescription className="text-muted-foreground">Receive deploy notifications via Slack, Discord, or any webhook URL</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2" data-testid="webhook-type-selector">
                  {[
                    { key: 'generic', label: 'Generic', desc: 'JSON POST' },
                    { key: 'slack', label: 'Slack', desc: 'Rich messages' },
                    { key: 'discord', label: 'Discord', desc: 'Embeds' }
                  ].map(wt => (
                    <button key={wt.key} onClick={() => { setWebhookType(wt.key); setWebhookSaved(false); }} data-testid={`webhook-type-${wt.key}`}
                      className={`flex-1 p-3 rounded-lg border-2 text-center transition-all ${webhookType === wt.key ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:border-input'}`}>
                      <div className={`text-sm font-medium ${webhookType === wt.key ? 'text-white' : 'text-muted-foreground'}`}>{wt.label}</div>
                      <div className="text-xs text-muted-foreground/70">{wt.desc}</div>
                    </button>
                  ))}
                </div>

                <div>
                  <Label className="text-foreground/80 text-sm">Webhook URL</Label>
                  <Input data-testid="webhook-url-input"
                    placeholder={webhookType === 'slack' ? 'https://hooks.slack.com/services/...' : webhookType === 'discord' ? 'https://discord.com/api/webhooks/...' : 'https://your-server.com/webhook'}
                    value={webhookUrl} onChange={(e) => { setWebhookUrl(e.target.value); setWebhookSaved(false); }}
                    className={`mt-1 font-mono text-sm ${inputClass}`} />
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleSaveWebhook} disabled={savingWebhook || !webhookUrl.trim()} className="bg-blue-600 hover:bg-blue-500" data-testid="save-webhook-btn">
                    {savingWebhook ? 'Saving...' : webhookSaved ? 'Saved' : 'Save Webhook'}
                  </Button>
                  {webhookSaved && webhookUrl && (
                    <Button variant="outline" onClick={handleTestWebhook} disabled={testingWebhook}
                      className="border-input text-foreground/80 hover:bg-accent" data-testid="test-webhook-btn">
                      {testingWebhook ? 'Sending...' : 'Send Test'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <SecurityTab
              passwordForm={passwordForm} setPasswordForm={setPasswordForm}
              saving={saving} handleChangePassword={handleChangePassword}
              socialProviders={socialProviders} connectedAccounts={connectedAccounts}
              handleConnectAccount={handleConnectAccount} inputClass={inputClass}
            />
          </TabsContent>

          {/* Domain Tab */}
          <TabsContent value="domain">
            <DomainTab
              customDomain={customDomain} setCustomDomain={setCustomDomain}
              domainSaved={domainSaved} setDomainSaved={setDomainSaved}
              savingDomain={savingDomain} handleSaveDomain={handleSaveDomain}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SettingsPage;
